import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import '../../styles/Admin.scss';

import rightArrow1 from '../../assets/rightLast.svg';
import rightArrow2 from '../../assets/right.svg';
import leftArrow1 from '../../assets/leftLast.svg';
import leftArrow2 from '../../assets/left.svg';
import iconDelete from '../../assets/icon_delete.svg';

import emoji1 from '../../assets/emojis/1.svg';
import emoji2 from '../../assets/emojis/2.svg';
import emoji3 from '../../assets/emojis/3.svg';
import emoji4 from '../../assets/emojis/4.svg';
import emoji5 from '../../assets/emojis/5.svg';
import emoji6 from '../../assets/emojis/6.svg';
import emoji7 from '../../assets/emojis/7.svg';
import AppSpinner from '../../components/AppSpinner';

interface DailyJournal {
  _id: string;
  user: {
    _id: string;
    displayName: string;
    userId: string;
    email: string;
  };
  emotion: number;
  content: string;
  createdAt: string;
}

const emotionIcons: { [key: number]: string } = {
  1: emoji1,
  2: emoji2,
  3: emoji3,
  4: emoji4,
  5: emoji5,
  6: emoji6,
  7: emoji7,
};

const AdminDailyJournalListPage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [journals, setJournals] = useState<DailyJournal[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [keyword, setKeyword] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalJournals, setTotalJournals] = useState<number>(0);
  const itemsPerPage = 10;

  const [selectedJournalIds, setSelectedJournalIds] = useState<string[]>([]);

  const fetchJournals = async (page: number) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params: any = {
        page,
        limit: itemsPerPage,
      };

      if (keyword) params.keyword = keyword;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const response = await axios.get('/api/admin/daily-journals', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params,
      });

      setJournals(response.data.journals);
      setTotalPages(response.data.totalPages);
      setTotalJournals(response.data.totalJournals);
      setCurrentPage(response.data.currentPage);
      setSelectedJournalIds([]);
    } catch (err) {
      console.error(err);
      setError('하루 한마디 목록을 불러오는 데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchJournals(1);
    }
  }, [isAuthenticated]);

  const handleSearch = () => {
    setCurrentPage(1);
    fetchJournals(1);
  };

  const handleReset = () => {
    setKeyword('');
    setStartDate('');
    setEndDate('');
    setCurrentPage(1);
    fetchJournals(1);
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      fetchJournals(page);
    }
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      const allIds = journals.map(journal => journal._id);
      setSelectedJournalIds(allIds);
    } else {
      setSelectedJournalIds([]);
    }
  };

  const handleSelectJournal = (id: string) => {
    setSelectedJournalIds(prev =>
      prev.includes(id) ? prev.filter(journalId => journalId !== id) : [...prev, id]
    );
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/admin/daily-journals/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      alert('삭제되었습니다.');
      fetchJournals(currentPage);
    } catch (err) {
      console.error(err);
      alert('삭제에 실패했습니다.');
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedJournalIds.length === 0) {
      alert('삭제할 항목을 선택해주세요.');
      return;
    }

    if (!window.confirm(`선택한 ${selectedJournalIds.length}개의 항목을 삭제하시겠습니까?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await Promise.all(
        selectedJournalIds.map(id =>
          axios.delete(`/api/admin/daily-journals/${id}`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          })
        )
      );
      alert('삭제되었습니다.');
      fetchJournals(currentPage);
    } catch (err) {
      console.error(err);
      alert('삭제 중 오류가 발생했습니다.');
    }
  };

  const stripHtml = (html: string) => {
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  const isAllSelected =
    journals.length > 0 && journals.every(journal => selectedJournalIds.includes(journal._id));

  if (loading && journals.length === 0) return <AppSpinner fullscreen tip="Loading..." />;

  return (
    <div className="admin-page">
      <h1>하루 한마디 관리</h1>

      <div className="search-filter-container">
        <div className="filter-group">
          <label>기간</label>
          <div className="date-range-group">
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
            <span> ~ </span>
            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
          </div>
        </div>

        <div className="filter-group">
          <label>검색</label>
          <div className="search-input-group">
            <input
              type="text"
              placeholder="내용, 작성자 이름/ID 검색"
              value={keyword}
              onChange={e => setKeyword(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
            />
          </div>
        </div>

        <div className="button-group">
          <button className="btn-search" onClick={handleSearch}>
            검색
          </button>
          <button className="btn-reset" onClick={handleReset}>
            초기화
          </button>
        </div>
      </div>

      <div className="list-info">
        총 게시물 <span className="count">{totalJournals}</span> | 페이지{' '}
        <span className="page">
          {currentPage} / {totalPages || 1}
        </span>
      </div>

      <div className="table-scroll">
        <table className="table">
          <colgroup>
            <col width="50px" />
            <col width="6%" />
            <col width="12%" />
            <col width="20%" />
            <col width="10%" />
            <col width="20%" />
            <col width="8%" />
          </colgroup>
          <thead>
            <tr>
              <th>
                <label className="custom-checkbox">
                  <input type="checkbox" checked={isAllSelected} onChange={handleSelectAll} />
                  <span className="checkmark"></span>
                </label>
              </th>
              <th>번호</th>
              <th>작성일시</th>
              <th>작성자</th>
              <th>아이디</th>
              <th>감정</th>
              <th>내용</th>
              <th>관리</th>
            </tr>
          </thead>
          <tbody>
            {journals.length > 0 ? (
              journals.map((journal, index) => (
                <tr key={journal._id}>
                  <td>
                    <label className="custom-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedJournalIds.includes(journal._id)}
                        onChange={() => handleSelectJournal(journal._id)}
                      />
                      <span className="checkmark"></span>
                    </label>
                  </td>
                  <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                  <td>
                    {new Date(journal.createdAt).toLocaleDateString()}{' '}
                    {new Date(journal.createdAt).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </td>
                  <td>{journal.user ? journal.user.displayName : '(알 수 없음)'}</td>
                  <td>{journal.user ? journal.user.userId : '-'}</td>
                  <td>
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      {emotionIcons[journal.emotion] ? (
                        <img
                          src={emotionIcons[journal.emotion]}
                          alt="emotion"
                          style={{ width: '30px', height: '30px' }}
                        />
                      ) : (
                        journal.emotion
                      )}
                    </div>
                  </td>
                  <td style={{ textAlign: 'left' }}>
                    <div
                      style={{
                        wordBreak: 'break-all',
                        whiteSpace: 'normal',
                      }}
                    >
                      {stripHtml(journal.content)}
                    </div>
                  </td>
                  <td>
                      <button className="btn-icon delete m-auto" onClick={() => handleDelete(journal._id)}>
                        <img src={iconDelete} alt="삭제" />
                      </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={8} className="no-data">
                  데이터가 없습니다.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>


      <div className="pagination-container">
        <div className="pagination">
          <button onClick={() => handlePageChange(1)} disabled={currentPage === 1}>
            <img src={leftArrow1} alt="처음" />
          </button>
          <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
            <img src={leftArrow2} alt="이전" />
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
            if (
              totalPages > 10 &&
              (page < currentPage - 2 || page > currentPage + 2) &&
              page !== 1 &&
              page !== totalPages
            ) {
              return null;
            }
            return (
              <button
                key={page}
                className={currentPage === page ? 'active' : ''}
                onClick={() => handlePageChange(page)}
              >
                {page}
              </button>
            );
          })}
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages || totalPages === 0}
          >
            <img src={rightArrow2} alt="다음" />
          </button>
          <button
            onClick={() => handlePageChange(totalPages)}
            disabled={currentPage === totalPages || totalPages === 0}
          >
            <img src={rightArrow1} alt="끝" />
          </button>
        </div>

        <div className="bottom-actions">
          <button className="btn-delete-selection" onClick={handleDeleteSelected}>
            <img src={iconDelete} alt="삭제" />
            삭제
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminDailyJournalListPage;
